import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { CentinelaAvatar } from "../../../components/base/CentinelaChat";
import { colors } from "../../../config/theme";
import { useTheme } from "../../../context/ThemeContext";
import { hexToRgbArray, scaleOf } from "../../../theme/preset";
import type { MonitoringNarrative } from "../services/mapCentinelaSummary";

export const BOOT_HEIGHT = 440;

const ACTS = {
  neurons: 2000,
  synapses: 1400,
  avatar: 520,
  heartbeat: 1400,
} as const;

const MARKS = {
  neurons: 0,
  synapses: ACTS.neurons,
  avatar: ACTS.neurons + ACTS.synapses,
  heartbeat: ACTS.neurons + ACTS.synapses + ACTS.avatar,
} as const;

export const BOOT_TOTAL = MARKS.heartbeat + ACTS.heartbeat;

const SKIP_ARM_MS = 700;

const SKIP_KEYS = new Set(["Escape", "Enter", " "]);

const RADIUS = 132;
const FOCAL = 560;
const NEURONS = 820;
const NEAR_PLANE = 70;
const DOT_RADIUS = { min: 0.5, extra: 0.95 } as const;
const NEIGHBOURHOOD = 0.155;
const MAX_SYNAPSES = 1500;
const CENTER_Y = 0.5;

const RINGS = [
  { radius: 1.08, tilt: 0.2, spin: 1.0, alpha: 0.5 },
  { radius: 1.18, tilt: -0.62, spin: -0.62, alpha: 0.34 },
  { radius: 1.28, tilt: 1.05, spin: 0.4, alpha: 0.22 },
] as const;

const RING_SEGMENTS = 90;

const SYNAPSE_BANDS = [
  { depth: 0.75, width: 1, alpha: 0.3 },
  { depth: 0.4, width: 0.35, alpha: 0.18 },
  { depth: 0, width: 0.4, alpha: 0.09 },
] as const;

interface BootPalette {
  neuron: string;
  synapse: string;
  pulse: string;
  ring: string;
  synapseAlpha: number;
  halo: string;
}

function paletteFor(isDarkMode: boolean): BootPalette {
  const primary = scaleOf(isDarkMode ? colors.dark : colors.light, "primary");
  const info = scaleOf(isDarkMode ? colors.dark : colors.light, "info");

  return isDarkMode
    ? {
        neuron: primary["400"],
        synapse: info["400"],
        pulse: primary["800"],
        ring: info["400"],
        synapseAlpha: 1.0,
        halo: hexToRgbArray(primary["400"]).join(", "),
      }
    : {
        neuron: primary["500"],
        synapse: info["500"],
        pulse: primary["700"],
        ring: info["500"],
        synapseAlpha: 1.5,
        halo: hexToRgbArray(primary["500"]).join(", "),
      };
}

interface Neuron {
  x: number;
  y: number;
  z: number;
  ox: number;
  oy: number;
  oz: number;
  r: number;
  delay: number;
}

interface Projected {
  x: number;
  y: number;
  scale: number;
  progress: number;
  visible: boolean;
}

function easeOutCubic(t: number) {
  return 1 - Math.pow(1 - t, 3);
}

function easeOutBack(t: number) {
  const c = 2.2;
  return 1 + (c + 1) * Math.pow(t - 1, 3) + c * Math.pow(t - 1, 2);
}

function segment(elapsed: number, start: number, duration: number) {
  return Math.max(0, Math.min((elapsed - start) / duration, 1));
}

function buildCore(count: number): Neuron[] {
  const phi = Math.PI * (3 - Math.sqrt(5));
  const points: Neuron[] = [];

  for (let i = 0; i < count; i++) {
    const y = 1 - (i / (count - 1)) * 2;
    const ring = Math.sqrt(Math.max(0, 1 - y * y));
    const theta = phi * i;
    const radius = RADIUS * (1 + (Math.random() - 0.5) * 0.06);

    const a = Math.random() * Math.PI * 2;
    const b = Math.acos(2 * Math.random() - 1);
    const away = 620 + Math.random() * 520;

    points.push({
      x: Math.cos(theta) * ring * radius,
      y: y * radius,
      z: Math.sin(theta) * ring * radius,
      ox: Math.sin(b) * Math.cos(a) * away,
      oy: Math.sin(b) * Math.sin(a) * away,
      oz: Math.cos(b) * away,
      r: DOT_RADIUS.min + Math.random() * DOT_RADIUS.extra,
      delay: Math.random() * 0.5,
    });
  }

  return points;
}

function weaveSynapses(neurons: Neuron[], threshold: number, cap: number) {
  const pairs: [number, number][] = [];
  const squared = threshold * threshold;

  for (let i = 0; i < neurons.length; i++) {
    for (let j = i + 1; j < neurons.length; j++) {
      const dx = neurons[i].x - neurons[j].x;
      const dy = neurons[i].y - neurons[j].y;
      const dz = neurons[i].z - neurons[j].z;
      if (dx * dx + dy * dy + dz * dz < squared) pairs.push([i, j]);
    }
  }

  if (pairs.length <= cap) return pairs;

  const step = pairs.length / cap;
  const thinned: [number, number][] = [];
  for (let k = 0; k < cap; k++) thinned.push(pairs[Math.floor(k * step)]);
  return thinned;
}

export interface CentinelaBootProps {
  narrative: MonitoringNarrative | null;
  onDone: () => void;
}

export default function CentinelaBoot({ narrative, onDone }: CentinelaBootProps) {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [elapsed, setElapsed] = useState(0);
  const [leaving, setLeaving] = useState(false);
  const closed = useRef(false);

  const palette = useRef(paletteFor(isDarkMode));
  palette.current = paletteFor(isDarkMode);

  const clock = useRef({ elapsed: 0, last: 0 });

  const close = useRef(() => {});
  close.current = () => {
    if (closed.current) return;
    closed.current = true;
    setLeaving(true);
    onDone();
  };

  const skippableAt = useRef(0);

  useEffect(() => {
    skippableAt.current = performance.now() + SKIP_ARM_MS;

    const onKeyDown = (event: KeyboardEvent) => {
      if (!SKIP_KEYS.has(event.code === "Space" ? " " : event.code)) return;
      if (performance.now() < skippableAt.current) return;
      close.current();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const skipByClick = () => {
    if (performance.now() < skippableAt.current) return;
    close.current();
  };

  useEffect(() => {
    const tick = setInterval(() => {
      setElapsed(clock.current.elapsed);
      if (clock.current.elapsed >= BOOT_TOTAL) close.current();
    }, 70);
    return () => clearInterval(tick);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const width = canvas.clientWidth;
    const height = canvas.clientHeight;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    ctx.scale(dpr, dpr);

    const cx = width / 2;
    const cy = height * CENTER_Y;

    const neurons = buildCore(NEURONS);
    const synapses = weaveSynapses(neurons, RADIUS * NEIGHBOURHOOD, MAX_SYNAPSES);

    const impulses = Array.from({ length: 16 }, () => ({
      pair: Math.floor(Math.random() * Math.max(synapses.length, 1)),
      phase: Math.random(),
      speed: 0.35 + Math.random() * 0.5,
    }));

    const view: Projected[] = neurons.map(() => ({
      x: 0,
      y: 0,
      scale: 0,
      progress: 0,
      visible: false,
    }));
    const order = neurons.map((_, i) => i);

    let frame = 0;

    const paint = (now: number) => {
      const pal = palette.current;

      if (!clock.current.last) clock.current.last = now;
      const delta = Math.min(now - clock.current.last, 250);
      clock.current.last = now;
      clock.current.elapsed += delta;
      const e = clock.current.elapsed;

      ctx.clearRect(0, 0, width, height);

      const forming = segment(e, MARKS.neurons, ACTS.neurons);
      const weaving = segment(e, MARKS.synapses, ACTS.synapses);
      const waking = segment(e, MARKS.avatar, ACTS.avatar);

      const ry = e / 4600;
      const rx = Math.sin(e / 7200) * 0.26;
      const cosY = Math.cos(ry);
      const sinY = Math.sin(ry);
      const cosX = Math.cos(rx);
      const sinX = Math.sin(rx);

      const project = (px: number, py: number, pz: number) => {
        const x1 = px * cosY + pz * sinY;
        const z1 = -px * sinY + pz * cosY;
        const y1 = py * cosX - z1 * sinX;
        const z2 = py * sinX + z1 * cosX;
        const den = FOCAL + z2;
        if (den <= NEAR_PLANE) return null;
        const scale = FOCAL / den;
        return { x: cx + x1 * scale, y: cy + y1 * scale, scale };
      };

      for (let i = 0; i < neurons.length; i++) {
        const n = neurons[i];
        const arrival = easeOutCubic(
          Math.max(0, Math.min((forming - n.delay) / (1 - n.delay), 1)),
        );
        const bx = n.ox + (n.x - n.ox) * arrival;
        const by = n.oy + (n.y - n.oy) * arrival;
        const bz = n.oz + (n.z - n.oz) * arrival;

        const breath = 1 + Math.sin(e / 1500) * 0.018 * arrival;

        const p = project(bx * breath, by * breath, bz * breath);
        const v = view[i];
        v.visible = p !== null;
        v.x = p ? p.x : 0;
        v.y = p ? p.y : 0;
        v.scale = p ? p.scale : 0;
        v.progress = arrival;
      }

      order.sort((a, b) => view[a].scale - view[b].scale);

      const opening = segment(e, MARKS.synapses, ACTS.synapses + 400);
      if (opening > 0) {
        ctx.lineWidth = 1;
        for (const ring of RINGS) {
          const spin = (e / 3400) * ring.spin;
          const cs = Math.cos(spin);
          const sn = Math.sin(spin);
          const ci = Math.cos(ring.tilt);
          const si = Math.sin(ring.tilt);
          const rad = RADIUS * ring.radius * easeOutCubic(opening);

          ctx.strokeStyle = pal.ring;
          ctx.globalAlpha = ring.alpha * opening * 0.75;
          ctx.beginPath();
          let broken = true;
          for (let k = 0; k <= RING_SEGMENTS; k++) {
            const a = (k / RING_SEGMENTS) * Math.PI * 2;
            const lx = Math.cos(a) * rad;
            const lz = Math.sin(a) * rad;
            const p = project(lx * cs + lz * sn, -lz * si, (-lx * sn + lz * cs) * ci);
            if (!p) {
              broken = true;
              continue;
            }
            if (broken) {
              ctx.moveTo(p.x, p.y);
              broken = false;
            } else {
              ctx.lineTo(p.x, p.y);
            }
          }
          ctx.stroke();
        }
        ctx.globalAlpha = 1;
      }

      if (weaving > 0) {
        ctx.strokeStyle = pal.synapse;
        ctx.lineWidth = 0.6;
        for (const band of SYNAPSE_BANDS) {
          ctx.globalAlpha = Math.min(weaving * band.alpha * pal.synapseAlpha, 1);
          ctx.beginPath();
          for (let k = 0; k < synapses.length; k++) {
            const [i, j] = synapses[k];
            const a = view[i];
            const b = view[j];
            if (!a.visible || !b.visible || a.progress < 0.97 || b.progress < 0.97) continue;
            const depth = ((a.scale + b.scale) / 2 - 0.72) / 0.55;
            if (depth < band.depth || depth >= band.depth + band.width) continue;
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
          }
          ctx.stroke();
        }
        ctx.globalAlpha = 1;
      }

      if (weaving > 0.4 && synapses.length) {
        ctx.fillStyle = pal.pulse;
        for (const im of impulses) {
          const [i, j] = synapses[im.pair % synapses.length];
          const a = view[i];
          const b = view[j];
          if (!a.visible || !b.visible || a.progress < 0.97 || b.progress < 0.97) continue;
          const t2 = (im.phase + (e / 1000) * im.speed) % 1;
          ctx.globalAlpha = Math.sin(t2 * Math.PI) * 0.9 * weaving;
          ctx.beginPath();
          ctx.arc(a.x + (b.x - a.x) * t2, a.y + (b.y - a.y) * t2, 1.8, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.globalAlpha = 1;
      }

      if (waking > 0) {
        const glow = ctx.createRadialGradient(cx, cy, 0, cx, cy, 112);
        glow.addColorStop(0, `rgba(${pal.halo}, ${0.22 * waking})`);
        glow.addColorStop(1, `rgba(${pal.halo}, 0)`);
        ctx.fillStyle = glow;
        ctx.beginPath();
        ctx.arc(cx, cy, 112, 0, Math.PI * 2);
        ctx.fill();
      }

      for (const i of order) {
        const v = view[i];
        if (!v.visible) continue;
        const n = neurons[i];
        const alpha =
          v.progress < 0.12
            ? v.progress / 0.12
            : Math.min(0.4 + (v.scale - 0.72) * 1.5, 1);
        ctx.globalAlpha = Math.max(0, Math.min(alpha, 1));
        ctx.fillStyle = v.scale > 1.02 ? pal.pulse : pal.neuron;
        ctx.beginPath();
        ctx.arc(v.x, v.y, Math.max(0.3, n.r * v.scale), 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;

      if (!closed.current) frame = requestAnimationFrame(paint);
    };

    frame = requestAnimationFrame(paint);

    const onVisibility = () => {
      cancelAnimationFrame(frame);
      clock.current.last = 0;
      if (!document.hidden && !closed.current) frame = requestAnimationFrame(paint);
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  const progress = segment(elapsed, MARKS.avatar, ACTS.avatar);
  const awake = Math.min(progress / 0.34, 1);
  const avatarScale = (0.62 + easeOutBack(progress) * 0.38) * 2.1;

  return (
    <div
      onClick={skipByClick}
      className="absolute inset-0 cursor-pointer select-none transition-opacity duration-500"
      style={{ opacity: leaving ? 0 : 1 }}
      role="status"
      aria-live="polite"
      aria-label={narrative?.title ?? t("monitoreoIa.boot.waking")}
      title={t("monitoreoIa.boot.skip")}
    >
      <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" />

      <div
        className="pointer-events-none absolute"
        style={{
          left: "50%",
          top: `${CENTER_Y * 100}%`,
          transform: `translate(-50%, -50%) scale(${avatarScale})`,
          opacity: awake,
        }}
      >
        <CentinelaAvatar size="xl" />
      </div>

      <div className="absolute right-2 top-1 font-mono text-[9px] max-sm:text-[10px] uppercase tracking-[0.14em] text-content-faint">
        {t("monitoreoIa.boot.skip")}
      </div>
    </div>
  );
}
